import React, { useEffect, useState } from 'react';
import { Modal } from '@fortress-technology-solutions/fortress-component-library/Molecules_Fortress';
import {
  Typography,
  Input,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import {
  MultiSelect,
  SingleSelect,
} from '@fortress-technology-solutions/fortress-component-library/Molecules';
import { FileDescriptionIcon } from '@fortress-technology-solutions/fortress-component-library/Icons';
import useIntl from '../../../hooks/useIntl';
import messages from './messages';
import useDownloadResidentLetterPDF from './hooks';

const REASON_OTHER_VALUE = 'other';

const ResidentLetterModal = ({
  open,
  closeModal,
  rentForecast,
  rows,
  isPercent = false,
}) => {
  const intl = useIntl();
  const [reasons, setReasons] = useState([]);
  const [otherText, setOtherText] = useState('');
  const [regionService, setRegionService] = useState(null);

  const hasOther = reasons.includes(REASON_OTHER_VALUE);
  const isValid =
    reasons.length > 0 &&
    (!hasOther || otherText.trim() !== '') &&
    regionService !== null;

  const { mutate: downloadPdf, isLoading } =
    useDownloadResidentLetterPDF(closeModal);

  useEffect(() => {
    if (!open) {
      setReasons([]);
      setOtherText('');
      setRegionService(null);
    }
  }, [open]);

  const handleGenerate = () => {
    if (!isValid) return;

    const requestOptions = {
      reasons,
      otherText,
      regionService,
    };

    downloadPdf({
      requestOptions,
      rentForecastId: rentForecast?.id,
      rows,
      isPercent,
    });
  };

  const reasonOptions = [
    {
      value: 'maintenance',
      text: intl.formatMessage(messages.residentLetterReasonMaintenance),
    },
    {
      value: 'fees',
      text: intl.formatMessage(messages.residentLetterReasonFees),
    },
    {
      value: 'taxes',
      text: intl.formatMessage(messages.residentLetterReasonTaxes),
    },
    {
      value: REASON_OTHER_VALUE,
      text: intl.formatMessage(messages.residentLetterReasonOther),
    },
  ];

  const regionOptions = [
    {
      value: 'northEast',
      text: intl.formatMessage(messages.residentRegionServiceNorthEast),
    },
    {
      value: 'midWest',
      text: intl.formatMessage(messages.residentRegionServiceMidWest),
    },
    {
      value: 'south',
      text: intl.formatMessage(messages.residentRegionServiceSouth),
    },
    {
      value: 'west',
      text: intl.formatMessage(messages.residentRegionServiceWest),
    },
  ];

  return (
    <Modal
      open={open}
      onClose={closeModal}
      title={intl.formatMessage(messages.residentLetterTitle)}
      icon={<FileDescriptionIcon />}
      actionsProps={[
        {
          children: intl.formatMessage(messages.residentLetterCancel),
          onClick: closeModal,
          disabled: isLoading,
        },
        {
          children: intl.formatMessage(messages.residentLetterGenerate),
          onClick: handleGenerate,
          disabled: !isValid || isLoading,
        },
      ]}
      ModalProps={{
        PaperProps: {
          sx: {
            width: {
              xs: 'calc(100% - 32px)',
              sm: 540,
            },
            maxHeight: 'calc(100vh - 100px)',
          },
        },
      }}
    >
      <>
        <Typography mb={3} variant="bodyMedium" display="block">
          {intl.formatMessage(messages.residentLetterDescription)}
        </Typography>

        <Typography variant="labelMedium" display="block">
          {intl.formatMessage(messages.residentLetterReasonsLabel)}
        </Typography>
        <Typography variant="bodySmall" mb={1} display="block">
          {intl.formatMessage(messages.residentLetterReasonsHelper)}
        </Typography>

        <MultiSelect
          name="reasons"
          options={reasonOptions}
          value={reasons}
          onChange={setReasons}
          placeholder={intl.formatMessage(
            messages.residentLetterReasonsPlaceholder,
          )}
          disabled={isLoading}
        />

        {hasOther && (
          <Input
            label={intl.formatMessage(messages.residentLetterOtherLabel)}
            value={otherText}
            onChange={(e) => setOtherText(e.target.value)}
            fullWidth
            sx={{ mt: 2 }}
            placeholder={intl.formatMessage(
              messages.residentLetterOtherPlaceholder,
            )}
            disabled={isLoading}
          />
        )}

        <SingleSelect
          name="regionService"
          label={intl.formatMessage(messages.residentRegionLabel)}
          value={regionService}
          options={regionOptions}
          onChange={setRegionService}
          placeholder={intl.formatMessage(messages.residentRegionPlaceholder)}
          disabled={isLoading}
        />
      </>
    </Modal>
  );
};

export default ResidentLetterModal;
